/**
 * Batch List Component
 * Collaborative view of all file upload batches
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Pagination,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  Add,
  Refresh,
  FilterList,
  Delete,
} from '@mui/icons-material';
import { useUpload } from '@/contexts/UploadContext';
import { useTranslation } from 'react-i18next';
import { formatDateDefault } from '@/utils/dateUtils';
import { useAuth } from '@/contexts/AuthContext';
import uploadApiService from '@/services/uploadApi';
import ConfirmDialog from '@/components/common/ConfirmDialog';

const BatchList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const {
    batches,
    loading,
    error,
    currentPage,
    totalPages,
    totalBatches,
    currentFilters,
    setCurrentPage,
    loadBatches,
    setCurrentFilters,
    clearError,
  } = useUpload();
  
  // Filters - initialize from context if available
  const [statusFilter, setStatusFilter] = useState(currentFilters.status_filter || '');
  const [clientFilter, setClientFilter] = useState(currentFilters.client || '');
  const [periodFilter, setPeriodFilter] = useState((currentFilters.period as string) || 'all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Sync local filters with context when context filters change (from outside)
  React.useEffect(() => {
    if (currentFilters.status_filter !== statusFilter || 
        currentFilters.client !== clientFilter || 
        currentFilters.period !== (periodFilter !== 'all' ? periodFilter : undefined)) {
      setStatusFilter(currentFilters.status_filter || '');
      setClientFilter(currentFilters.client || '');
      setPeriodFilter((currentFilters.period as string) || 'all');
    }
  }, [currentFilters.status_filter, currentFilters.client, currentFilters.period]);
  
  // Delete batch
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Apply filters
  const handleApplyFilters = () => {
    const filters = {
      status_filter: statusFilter || undefined,
      client: clientFilter || undefined,
      period: periodFilter !== 'all' ? (periodFilter as any) : undefined,
    };
    // Update context filters and load batches
    setCurrentFilters(filters);
    loadBatches(filters);
  };
  
  // Clear filters
  const handleClearFilters = () => {
    setStatusFilter('');
    setClientFilter('');
    setPeriodFilter('all');
    const emptyFilters = {};
    setCurrentFilters(emptyFilters);
    loadBatches(emptyFilters);
  };
  
  // Translate status label
  const translateStatus = (status: string): string => {
    return t(`upload.details.status.${status}`, status);
  };
  
  // Get status color
  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'primary';
      case 'uploading':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Format date
  // Using Montreal timezone from utils
  const formatDate = formatDateDefault;
  
  // Handle delete batch
  const handleDeleteClick = (batchId: string) => {
    setBatchToDelete(batchId);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!batchToDelete) return;
    
    setDeleting(true);
    try {
      await uploadApiService.deleteBatch(batchToDelete);
      setDeleteDialogOpen(false);
      setBatchToDelete(null);
      loadBatches(); // Refresh list with current filters
    } catch (err: any) {
      console.error('Failed to delete batch:', err);
      alert(err.detail || t('upload.list.delete_error', 'Failed to delete batch'));
    } finally {
      setDeleting(false);
    }
  };
  
  // Check if user can delete batch (admin only for now)
  const canDeleteBatch = (): boolean => {
    if (!currentUser) return false;
    // Admin can delete any batch
    return currentUser.role === 'admin';
  };
  
  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {t('upload.list.title', 'File Upload Batches')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('upload.list.subtitle', 'Collaborative view - All batches from all users')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title={t('upload.list.filters', 'Toggle Filters')}>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterList />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('upload.list.refresh', 'Refresh')}>
            <IconButton onClick={() => loadBatches()}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/upload/create')}
          >
            {t('upload.list.create', 'New Batch')}
          </Button>
        </Box>
      </Box>
      
      {/* Filters */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                select
                size="small"
                label={t('upload.list.status', 'Status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">{t('upload.list.all_status', 'All')}</MenuItem>
                <MenuItem value="uploading">{t('upload.list.uploading', 'Uploading')}</MenuItem>
                <MenuItem value="processing">{t('upload.list.processing', 'Processing')}</MenuItem>
                <MenuItem value="completed">{t('upload.list.completed', 'Completed')}</MenuItem>
                <MenuItem value="failed">{t('upload.list.failed', 'Failed')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label={t('upload.list.client', 'Client')}
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                placeholder={t('upload.list.client_placeholder', 'Search by client')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                select
                size="small"
                label={t('upload.list.period', 'Period')}
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
              >
                <MenuItem value="all">{t('upload.list.all_time', 'All Time')}</MenuItem>
                <MenuItem value="today">{t('upload.list.today', 'Today')}</MenuItem>
                <MenuItem value="week">{t('upload.list.week', 'This Week')}</MenuItem>
                <MenuItem value="month">{t('upload.list.month', 'This Month')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth variant="contained" onClick={handleApplyFilters}>
                  {t('upload.list.apply', 'Apply')}
                </Button>
                <Button fullWidth variant="outlined" onClick={handleClearFilters}>
                  {t('upload.list.clear', 'Clear')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
      
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}
      
      {/* Loading - Disabled for better UX during auto-refresh */}
      {/* {loading && <LinearProgress sx={{ mb: 2 }} />} */}
      
      {/* Batch Count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('upload.list.showing', { count: batches.length, total: totalBatches })}
      </Typography>
      
      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('upload.list.table.client', 'Client')}</TableCell>
              <TableCell>{t('upload.list.table.uploaded_by', 'Uploaded By')}</TableCell>
              <TableCell>{t('upload.list.table.status', 'Status')}</TableCell>
              <TableCell align="right">{t('upload.list.table.files', 'Files')}</TableCell>
              <TableCell align="right">{t('upload.list.table.articles', 'Retombées')}</TableCell>
              <TableCell>{t('upload.list.table.progress', 'Progress')}</TableCell>
              <TableCell>{t('upload.list.table.created', 'Created')}</TableCell>
              <TableCell align="center">{t('upload.list.table.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    {t('upload.list.no_batches', 'No batches found')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              batches.map((batch) => (
                <TableRow key={batch.batch_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {batch.client_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {batch.batch_id.slice(0, 20)}...
                    </Typography>
                  </TableCell>
                  <TableCell>{batch.uploaded_by_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={translateStatus(batch.status)}
                      size="small"
                      color={getStatusColor(batch.status)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {batch.files_processed} / {batch.total_files}
                  </TableCell>
                  <TableCell align="right">{batch.articles_extracted}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={batch.progress_percentage}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {batch.progress_percentage}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(batch.created_at)}</Typography>
                    {batch.completed_at && (
                      <Typography variant="caption" color="text.secondary">
                        {t('upload.list.completed', 'Done')}: {formatDate(batch.completed_at)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title={t('upload.list.view_details', 'View Details')}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/upload/batch/${batch.batch_id}`)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {canDeleteBatch() && (
                        <Tooltip title={t('upload.list.delete', 'Delete Batch')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(batch.batch_id)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
          />
        </Box>
      )}
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setBatchToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('upload.list.delete_confirm_title', 'Delete Batch')}
        message={t(
          'upload.list.delete_confirm_message',
          'Are you sure you want to delete this batch? This will also delete all associated files. This action cannot be undone.'
        )}
        confirmText={t('upload.list.delete', 'Delete')}
        severity="error"
        isLoading={deleting}
      />
    </Box>
  );
};

export default BatchList;

